import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
// Api
import { GET_TICKETING_SETTING } from 'api/ticketing/queries';
// Types
import {
  GetTicketingSetting,
  GetTicketingSetting_getTicketingSetting,
} from 'api/ticketing/types/GetTicketingSetting';
// Hooks
import { useSaveTicketingSetting } from './hooks/useSaveTicketingSetting';
// Helpers
import { formatMoney, getTicketingErrorMessage } from 'pages/Events/helpers';
import {
  getBuyerTotal,
  getChangedInput,
  TicketingSettingsFormValues,
  toFormValues,
} from './helpers';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
// Styles
import styles from './TicketingSettings.module.scss';

const { Title, Text, Paragraph } = Typography;

type Setting = GetTicketingSetting_getTicketingSetting;

type SwitchName =
  | 'isLaunched'
  | 'isEnabled'
  | 'salesEnabled'
  | 'transfersEnabled'
  | 'gateEnabled'
  | 'workersEnabled';

type SwitchConfig = {
  name: SwitchName;
  label: string;
  /** What the switch allows while it is on. */
  description: string;
  /** What stops when it is turned off; shown before saving. */
  whenOff: string;
};

const MAIN_SWITCH: SwitchConfig = {
  name: 'isLaunched',
  label: 'Ticketing',
  description:
    'The main switch. While it is off, ticketing is hidden everywhere: the website, the mobile app and backoffice show no events, tickets or door scanner, and nobody can use them. Turn it on to launch ticketing.',
  whenOff:
    'Ticketing disappears from the website, the mobile app and backoffice. Nobody can buy, see or scan tickets until it is turned back on.',
};

const CONTROL_SWITCHES: SwitchConfig[] = [
  {
    name: 'isEnabled',
    label: 'Event setup',
    description:
      'Hosts and admins can create and edit events, ticket tiers, venues, presale codes and comps.',
    whenOff:
      'Nobody can create or change events and no new checkouts start. Tickets already sold, door scanning and refunds keep working.',
  },
  {
    name: 'salesEnabled',
    label: 'Ticket sales',
    description: 'Fans can reserve and buy tickets.',
    whenOff: 'Sales pause. Orders that are already being paid still complete.',
  },
  {
    name: 'transfersEnabled',
    label: 'Ticket transfers',
    description: 'Fans can send a ticket to someone else.',
    whenOff:
      'Transfers pause. Invitations already sent wait until it is back on.',
  },
  {
    name: 'gateEnabled',
    label: 'Door scanning',
    description: 'Staff can scan tickets at the door.',
    whenOff: 'Tickets cannot be checked or scanned at the door.',
  },
  {
    name: 'workersEnabled',
    label: 'Background work',
    description:
      'Charges cards, refunds orders, sends confirmation emails and releases expired holds. Keep it on whenever ticketing is live.',
    whenOff:
      'Cards are not charged, refunds and order emails stop, and expired holds are not released.',
  },
];

const SWITCHES = [MAIN_SWITCH, ...CONTROL_SWITCHES];

const EXAMPLE_TICKET_PRICE = 50;

const TicketingSettings = (): JSX.Element => {
  const [form] = Form.useForm<TicketingSettingsFormValues>();
  const [saved, setSaved] = useState<TicketingSettingsFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetTicketingSetting>(
    GET_TICKETING_SETTING,
    { fetchPolicy: 'network-only' }
  );
  const { saveSetting, busy: saving } = useSaveTicketingSetting();

  const applySaved = useCallback(
    (setting: Setting): void => {
      const values = toFormValues(setting);
      setSaved(values);
      form.setFieldsValue(values);
      setIsDirty(false);
    },
    [form]
  );

  useEffect(() => {
    if (data?.getTicketingSetting) {
      applySaved(data.getTicketingSetting);
    }
  }, [data, applySaved]);

  const save = async (values: TicketingSettingsFormValues): Promise<void> => {
    if (!saved) {
      return;
    }

    try {
      const result = await saveSetting(getChangedInput(saved, values));

      if (result) {
        applySaved(result);
      }

      successNotification('Ticketing settings saved');
    } catch (err) {
      errorNotification(
        getTicketingErrorMessage(err, 'Failed to save the ticketing settings')
      );
    }
  };

  const handleFinish = (values: TicketingSettingsFormValues): void => {
    const switchedOff = SWITCHES.filter(
      ({ name }) => saved?.[name] && !values[name]
    );

    if (!switchedOff.length) {
      save(values);
      return;
    }

    Modal.confirm({
      title: `Turn off ${switchedOff
        .map(({ label }) => label.toLowerCase())
        .join(', ')}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <ul className={styles.confirmList}>
          {switchedOff.map(({ name, label, whenOff }) => (
            <li key={name}>
              <Text strong>{label}:</Text> {whenOff}
            </li>
          ))}
        </ul>
      ),
      okText: 'Turn off and save',
      okButtonProps: { danger: true },
      onOk: () => save(values),
    });
  };

  const handleReset = (): void => {
    if (saved) {
      form.setFieldsValue(saved);
      setIsDirty(false);
    }
  };

  if (error) {
    return (
      <LoadErrorAlert
        message="Failed to load the ticketing settings"
        error={error}
        onRetry={refetch}
      />
    );
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  return (
    <Form<TicketingSettingsFormValues>
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={() => setIsDirty(true)}
      className={styles.root}
      requiredMark={false}
    >
      <Card className={styles.card}>
        <div className={styles.switchRow}>
          <div>
            <div className={styles.mainTitle}>
              <Title level={4}>{MAIN_SWITCH.label}</Title>
              {saved?.isLaunched ? (
                <Tag color="green">Live</Tag>
              ) : (
                <Tag>Hidden</Tag>
              )}
            </div>
            <Paragraph type="secondary" className={styles.switchHint}>
              {MAIN_SWITCH.description}
            </Paragraph>
          </div>
          <Form.Item name={MAIN_SWITCH.name} valuePropName="checked" noStyle>
            <Switch aria-label={MAIN_SWITCH.label} />
          </Form.Item>
        </div>
      </Card>

      <Card className={styles.card}>
        <Title level={4}>Controls</Title>
        <Paragraph type="secondary">
          Pause one part of ticketing without hiding the rest. They apply to
          every host and to admins too
          {saved?.isLaunched ? '.' : ', and take effect once Ticketing is on.'}
        </Paragraph>
        <div className={styles.switches}>
          {CONTROL_SWITCHES.map(({ name, label, description }) => (
            <div className={styles.switchRow} key={name}>
              <div>
                <Text strong>{label}</Text>
                <Paragraph type="secondary" className={styles.switchHint}>
                  {description}
                </Paragraph>
              </div>
              <Form.Item name={name} valuePropName="checked" noStyle>
                <Switch aria-label={label} />
              </Form.Item>
            </div>
          ))}
        </div>
      </Card>

      <Card className={styles.card}>
        <Title level={4}>Checkout</Title>
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Form.Item
              name="holdMinutes"
              label="Hold duration (minutes)"
              extra="How long tickets stay reserved while the buyer pays. When the hold expires the tickets go back on sale."
              rules={[
                { required: true, message: 'Enter the hold duration' },
                {
                  type: 'number',
                  min: 1,
                  max: 60,
                  message: 'Between 1 and 60 minutes',
                },
              ]}
            >
              <InputNumber min={1} max={60} step={1} className={styles.input} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="paymentDeadlineMinutes"
              label="Payment deadline (minutes)"
              extra="How long a payment still in progress keeps its tickets before the order is released."
              rules={[
                { required: true, message: 'Enter the payment deadline' },
                {
                  type: 'number',
                  min: 1,
                  max: 60,
                  message: 'Between 1 and 60 minutes',
                },
              ]}
            >
              <InputNumber min={1} max={60} step={1} className={styles.input} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="transferWindowHours"
              label="Transfer window (hours)"
              extra="How long a ticket transfer invitation stays open."
              rules={[
                { required: true, message: 'Enter the transfer window' },
                {
                  type: 'number',
                  min: 1,
                  max: 168,
                  message: 'Between 1 and 168 hours',
                },
              ]}
            >
              <InputNumber
                min={1}
                max={168}
                step={1}
                precision={0}
                className={styles.input}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card className={styles.card}>
        <Title level={4}>Prices and fees</Title>
        <Row gutter={24}>
          <Col xs={24} md={6}>
            <Form.Item
              name="minTicketPrice"
              label="Minimum ticket price ($)"
              rules={[
                { required: true, message: 'Enter the minimum price' },
                {
                  type: 'number',
                  min: 0.5,
                  max: 1000000,
                  message: 'Between $0.50 and $1,000,000',
                },
              ]}
            >
              <InputNumber
                min={0.5}
                step={0.5}
                precision={2}
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="maxTicketPrice"
              label="Maximum ticket price ($)"
              dependencies={['minTicketPrice']}
              rules={[
                { required: true, message: 'Enter the maximum price' },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    value === undefined ||
                    value === null ||
                    value >= getFieldValue('minTicketPrice')
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Must be at least the minimum price')
                        ),
                }),
              ]}
            >
              <InputNumber
                min={0.5}
                max={1000000}
                step={10}
                precision={2}
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="serviceFeePercent"
              label="Service fee (%)"
              extra="Of the ticket subtotal after discounts."
              rules={[
                { required: true, message: 'Enter the service fee' },
                {
                  type: 'number',
                  min: 0,
                  max: 30,
                  message: 'Between 0% and 30%',
                },
              ]}
            >
              <InputNumber
                min={0}
                max={30}
                step={0.5}
                precision={2}
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="serviceFeePerTicket"
              label="Service fee per ticket ($)"
              extra="Added on top of the percentage."
              rules={[
                { required: true, message: 'Enter the per-ticket fee' },
                {
                  type: 'number',
                  min: 0,
                  max: 100,
                  message: 'Between $0 and $100',
                },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                precision={2}
                className={styles.input}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          noStyle
          shouldUpdate={(prev, next) =>
            prev.serviceFeePercent !== next.serviceFeePercent ||
            prev.serviceFeePerTicket !== next.serviceFeePerTicket
          }
        >
          {({ getFieldValue }) => (
            <Text type="secondary">
              A {formatMoney(EXAMPLE_TICKET_PRICE)} ticket costs the buyer{' '}
              {formatMoney(
                getBuyerTotal(
                  EXAMPLE_TICKET_PRICE,
                  getFieldValue('serviceFeePercent'),
                  getFieldValue('serviceFeePerTicket')
                )
              )}
              . Holds already started keep the fee they were priced at.
            </Text>
          )}
        </Form.Item>
      </Card>

      <Card className={styles.card}>
        <Title level={4}>Event defaults</Title>
        <Paragraph type="secondary">
          Used when a host doesn&apos;t set these on the event.
        </Paragraph>
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Form.Item
              name="defaultEventDurationHours"
              label="Event length (hours)"
              rules={[
                { required: true, message: 'Enter the event length' },
                {
                  type: 'number',
                  min: 1,
                  max: 168,
                  message: 'Between 1 and 168 hours',
                },
              ]}
            >
              <InputNumber
                min={1}
                max={168}
                step={1}
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="defaultSalesCloseAfterStartHours"
              label="Sales close (hours after start)"
              extra="Negative closes sales before the event starts."
              dependencies={['maxSalesCloseAfterStartHours']}
              rules={[
                { required: true, message: 'Enter when sales close' },
                {
                  type: 'number',
                  min: -24,
                  max: 168,
                  message: 'Between -24 and 168 hours',
                },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    value === undefined ||
                    value === null ||
                    value <= getFieldValue('maxSalesCloseAfterStartHours')
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Must not be later than the latest close')
                        ),
                }),
              ]}
            >
              <InputNumber
                min={-24}
                max={168}
                step={1}
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="maxSalesCloseAfterStartHours"
              label="Latest sales close (hours after start)"
              extra="Hosts can't keep sales open later than this."
              rules={[
                { required: true, message: 'Enter the latest sales close' },
                {
                  type: 'number',
                  min: 0,
                  max: 168,
                  message: 'Between 0 and 168 hours',
                },
              ]}
            >
              <InputNumber
                min={0}
                max={168}
                step={1}
                className={styles.input}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <div className={styles.footer}>
        <Button onClick={handleReset} disabled={!isDirty || saving}>
          Discard changes
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          disabled={!isDirty}
        >
          Save settings
        </Button>
      </div>
    </Form>
  );
};

export default TicketingSettings;
